import React, { useState, useMemo } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  Checkbox,
  Chip,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { useApiContext } from "../../../Utils/ApiContext/ApiContext";

const ClinicServices = ({ onServiceClick }) => {
  const { services, loading } = useApiContext();
  const [searchQuery, setSearchQuery] = useState("");

  // **Grouping Services by `service_no` and combining `service_subtype`**
  const groupedServices = useMemo(() => {
    const serviceMap = {};

    services.forEach((service) => {
      if (!serviceMap[service.service_no]) {
        serviceMap[service.service_no] = {
          service_no: service.service_no,
          service: service.service,
          has_consent_treatment: service.has_consent_treatment,
          has_intake_form: service.has_intake_form,
          has_post_treatment: service.has_post_treatment,
          has_pre_treatment: service.has_pre_treatment,
          has_treatment_form: service.has_treatment_form,
          unit_cost: service.unit_cost,
          status: service.status,
          service_subtypes: [service.service_subtype],
        };
      } else {
        serviceMap[service.service_no].service_subtypes.push(service.service_subtype);
      }
    });

    return Object.values(serviceMap);
  }, [services]);

  // **Filtering services based on search input**
  const filteredServices = groupedServices.filter((service) =>
    service.service.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box>

      {/* Search Filter */}
      <TextField
        label="Search by Service"
        variant="outlined"
        fullWidth
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ marginBottom: 2 }}
      />

      {/* If No Services */}
      {filteredServices.length === 0 ? (
        <Typography variant="h6" color="textSecondary" sx={{ textAlign: "center", marginTop: 3 }}>
          No services available.
        </Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 800 }}>
            <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Service No</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Service</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Consent Form</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Intake Form</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Post Treatment</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Pre Treatment</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Treatment Form</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Unit Cost ($)</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Subservices</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredServices.map((service) => (
                <TableRow key={service.service_no} sx={{ "&:hover": { backgroundColor: "#f0f8ff" } }} onClick={() => onServiceClick(service)}>
                  <TableCell>{service.service_no}</TableCell>
                  <TableCell>{service.service}</TableCell>

                  {/* Consent Form */}
                  <TableCell>
                    <Chip
                      icon={service.has_consent_treatment === "1" ? <CheckCircleIcon /> : <CancelIcon />}
                      label={service.has_consent_treatment === "1" ? "Available" : "Not Available"}
                      color={service.has_consent_treatment === "1" ? "success" : "error"}
                      variant="outlined"
                    />
                  </TableCell>

                  {/* Intake Form */}
                  <TableCell>
                    <Chip
                      icon={service.has_intake_form === "1" ? <CheckCircleIcon /> : <CancelIcon />}
                      label={service.has_intake_form === "1" ? "Available" : "Not Available"}
                      color={service.has_intake_form === "1" ? "success" : "error"}
                      variant="outlined"
                    />
                  </TableCell>

                  {/* Post Treatment */}
                  <TableCell>
                    <Chip
                      icon={service.has_post_treatment === "1" ? <CheckCircleIcon /> : <CancelIcon />}
                      label={service.has_post_treatment === "1" ? "Available" : "Not Available"}
                      color={service.has_post_treatment === "1" ? "success" : "error"}
                      variant="outlined"
                    />
                  </TableCell>

                  {/* Pre Treatment */}
                  <TableCell>
                    <Chip
                      icon={service.has_pre_treatment === "1" ? <CheckCircleIcon /> : <CancelIcon />}
                      label={service.has_pre_treatment === "1" ? "Available" : "Not Available"}
                      color={service.has_pre_treatment === "1" ? "success" : "error"}
                      variant="outlined"
                    />
                  </TableCell>

                  {/* Treatment Form */}
                  <TableCell>
                    <Chip
                      icon={service.has_treatment_form === "1" ? <CheckCircleIcon /> : <CancelIcon />}
                      label={service.has_treatment_form === "1" ? "Available" : "Not Available"}
                      color={service.has_treatment_form === "1" ? "success" : "error"}
                      variant="outlined"
                    />
                  </TableCell>

                  {/* Unit Cost */}
                  <TableCell>${service.unit_cost}</TableCell>

                  {/* Status (Non-Editable Checkbox) */}
                  <TableCell>
                    <Checkbox checked={service.status === "1"} disabled />
                  </TableCell>

                  {/* Subservices - Tooltip for better visibility */}
                  <TableCell>
                    <Chip
                      label={`${service.service_subtypes.length} Subservices`}
                      color="primary"
                      sx={{ cursor: "pointer" }}
                      title={service.service_subtypes.join(", ")}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ClinicServices;
